import React, { useState, useEffect, useCallback } from 'react';
import { SavedVisualization, StorageManager, VisualizationMetadata } from '../utils/storageManager';
import './HistorySidebar.css';

interface HistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadVisualization: (visualization: SavedVisualization) => void;
  onDeleteVisualization?: (id: string) => void;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  isOpen,
  onClose,
  onLoadVisualization,
  onDeleteVisualization
}) => {
  const [visualizations, setVisualizations] = useState<SavedVisualization[]>([]);
  const [metadata, setMetadata] = useState<VisualizationMetadata | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const storage = StorageManager.getInstance();

  const loadVisualizations = useCallback(() => {
    setLoading(true);
    try {
      const saved = storage.getAllVisualizations();
      const meta = storage.getMetadata();
      setVisualizations(saved);
      setMetadata(meta);
    } catch (error) {
      console.error('Error loading visualizations:', error);
    } finally {
      setLoading(false);
    }
  }, [storage]);

  useEffect(() => {
    if (isOpen) {
      loadVisualizations();
    }
  }, [isOpen, loadVisualizations]);

  const handleDelete = async (id: string) => {
    try {
      await storage.deleteVisualization(id);
      loadVisualizations();
      onDeleteVisualization?.(id);
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting visualization:', error);
    }
  };

  const handleExport = async (visualization: SavedVisualization) => {
    try {
      const blob = await storage.exportVisualization(visualization.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${visualization.name.replace(/[^a-z0-9]/gi, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting visualization:', error);
    }
  };

  const filteredAndSortedVisualizations = visualizations
    .filter(viz => 
      viz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      viz.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      viz.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      } else {
        return a.name.localeCompare(b.name);
      }
    });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="history-sidebar-overlay" onClick={onClose}>
      <div className="history-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="sidebar-header">
          <div className="header-title">
            <h3>Saved Visualizations</h3>
            <button className="close-button" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {metadata && (
            <div className="storage-info">
              <div className="storage-stats">
                <span>{metadata.totalSaved} saved</span>
                <span>{formatFileSize(metadata.storageUsed)} used</span>
              </div>
              <div className="storage-bar">
                <div 
                  className="storage-progress" 
                  style={{ width: `${Math.min((metadata.storageUsed / (50 * 1024 * 1024)) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className="search-controls">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search visualizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
              className="sort-select"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>

        <div className="sidebar-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading visualizations...</p>
            </div>
          ) : filteredAndSortedVisualizations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 17H7A5 5 0 0 1 7 7h2m0 10v-5a5 5 0 1 1 10 0v5a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2Z"/>
                  <path d="M9 12h6"/>
                </svg>
              </div>
              <h4>No visualizations found</h4>
              <p>
                {searchQuery 
                  ? "Try adjusting your search terms"
                  : "Save your first visualization to see it here"
                }
              </p>
            </div>
          ) : (
            <div className="visualizations-list">
              {filteredAndSortedVisualizations.map((viz) => (
                <div key={viz.id} className="visualization-item">
                  <div className="viz-main" onClick={() => onLoadVisualization(viz)}>
                    <div className="viz-info">
                      <h4>{viz.name}</h4>
                      <p className="viz-file">{viz.fileName}</p>
                      {viz.description && (
                        <p className="viz-description">{viz.description}</p>
                      )}
                      <div className="viz-meta">
                        <span className="viz-date">{formatDate(viz.updatedAt)}</span>
                        <span className="viz-rows">{viz.data.length} rows</span>
                      </div>
                    </div>
                    <div className="viz-preview">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18"/>
                        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                      </svg>
                    </div>
                  </div>

                  <div className="viz-actions">
                    <button
                      className="action-button export"
                      onClick={() => handleExport(viz)}
                      title="Export"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,5 17,10"/>
                        <line x1="12" y1="5" x2="12" y2="15"/>
                      </svg>
                    </button>
                    <button
                      className="action-button delete"
                      onClick={() => setDeleteId(viz.id)}
                      title="Delete"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6"/>
                        <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {deleteId && (
          <div className="delete-confirmation">
            <div className="delete-dialog">
              <h4>Delete Visualization?</h4>
              <p>This action cannot be undone. The visualization will be permanently deleted.</p>
              <div className="delete-actions">
                <button
                  className="cancel-delete"
                  onClick={() => setDeleteId(null)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete"
                  onClick={() => handleDelete(deleteId)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorySidebar;
